import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useGoogleLogin } from '@react-oauth/google';
import { api } from '../services/api';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [requestId, setRequestId] = useState<string | null>(null);

  useEffect(() => {
    // Get requestId from URL parameters or localStorage
    const urlRequestId = searchParams.get('requestId');
    const storedRequestId = localStorage.getItem('loginRequestId');
    const currentRequestId = urlRequestId || storedRequestId;
    
    if (!currentRequestId) {
      setError('No login request ID found. Please start from the login link provided.');
      return;
    }
    
    // Verify the login request
    const verifyLoginRequest = async () => {
      try {
        setIsLoading(true);
        const response = await api.checkLoginRequest(currentRequestId);
        
        if (!response.valid) {
          setError(response.error || 'Invalid login request');
          return;
        }
        
        setRequestId(currentRequestId);
        // Store it in localStorage in case it came from URL
        if (urlRequestId) {
          localStorage.setItem('loginRequestId', urlRequestId);
        }
        
        setIsLoading(false);
      } catch (error) {
        console.error('Error verifying login request:', error);
        setError('Failed to verify login request');
        setIsLoading(false);
      }
    };

    verifyLoginRequest();
  }, [searchParams]);

  // Handle Google login with the library
  const login = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        setIsLoading(true);
        
        // Get user info from Google with the access token
        const userInfoResponse = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
          headers: { Authorization: `Bearer ${tokenResponse.access_token}` },
        });
        
        if (!userInfoResponse.ok) {
          throw new Error('Failed to get user information');
        }
        
        const userInfo = await userInfoResponse.json();
        
        // Complete the login process with our backend
        if (requestId) {
          await api.completeLogin(requestId, userInfo.sub, userInfo.name, userInfo.email, userInfo.picture);
          
          // Get the authentication token
          const statusResponse = await api.checkLoginStatus(requestId);
          
          if (statusResponse.status === 'completed' && statusResponse.token) {
            // Store the token in localStorage
            localStorage.setItem('authToken', statusResponse.token);
            
            // Redirect to home page
            navigate(`/callback?code=${tokenResponse.access_token}&state=${requestId}`);
          } else {
            throw new Error('Failed to retrieve authentication token');
          }
        }
        
        setIsLoading(false);
      } catch (error) {
        console.error('Error during login process:', error);
        setError('Authentication failed. Please try again.');
        setIsLoading(false);
      }
    },
    onError: (errorResponse) => {
      console.error('Google login error:', errorResponse);
      setError('Google authentication failed. Please try again.');
    },
    flow: 'implicit', // Use implicit flow for simplicity
  });

  if (isLoading) {
    return (
      <div className="login-page">
        <div className="container">
          <h2>Setting up login...</h2>
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="container">
        <div className="logo-container">
          <img src="/logo.png" alt="Logo" />
        </div>
        
        <h2>Login to Your Account</h2>
        
        {error ? (
          <div className="error-message">
            <p>{error}</p>
            <button onClick={() => navigate('/')}>Return to Home</button>
          </div>
        ) : (
          <>
            <button 
              className="login-button"
              onClick={() => login()}
              disabled={isLoading || !requestId}
            >
              Login with Google
            </button>
            
            <p className="info-text">
              Your information is secure and will only be used for authentication.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default LoginPage; 